import DeleteAlertDialog from "@/components/DeleteAlertDialog";
import { Button } from "@/components/ui/button";
import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import UserProfile from "@/domain/entities/user/UserProfile";
import useDeleteAgendaEvent from "@/hooks/agenda/useDeleteAgendaEvent";
import { isEventPast } from "@/lib/utils";
import { setOpenedEvent } from "@/stores/eventStore";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import EventInvitation from "./EventInvitation";

type EventContentFooterProps = {
    user: UserProfile;
    updatedEvent: AgendaItem;
    setUpdatedEvent: (event: AgendaItem) => void;
    setIsEditing?: (isEditing: boolean) => void;
    editionEnabled?: boolean;
};

const EventContentFooter = ({
    user,
    updatedEvent,
    setUpdatedEvent,
    setIsEditing,
    editionEnabled,
}: EventContentFooterProps) => {
    const { deleteAgendaEvent } = useDeleteAgendaEvent();
    const { t } = useTranslation();

    if (updatedEvent.userIsOrganizer(user) && !editionEnabled) {
        return null;
    }

    return (
        <div className="event-footer border-t border-white/50 bg-white/10">
            {!updatedEvent.userIsOrganizer(user) &&
                !updatedEvent.isCancelled &&
                !isEventPast(updatedEvent) && (
                    <EventInvitation
                        setUpdatedEvent={(eventUpdated) =>
                            setUpdatedEvent(eventUpdated as AgendaItem)
                        }
                        updatedEvent={updatedEvent}
                    />
                )}
            {updatedEvent.userIsOrganizer(user) && editionEnabled && (
                <div className="flex justify-end gap-2 p-1">
                    {setIsEditing && (
                        <Button
                            className="rounded-full px-2 py-1 hover:bg-white/20"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsEditing(true);
                            }}
                            variant="invisible"
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                    )}
                    <DeleteAlertDialog
                        description={t(
                            "dashboard.widgets.calendar.event.deleteAlert.description",
                        )}
                        handleDeleteAction={() => {
                            deleteAgendaEvent.mutate(updatedEvent.id);
                            setOpenedEvent(undefined);
                        }}
                        title={t(
                            "dashboard.widgets.calendar.event.deleteAlert.title",
                        )}
                        trigger={
                            <Button
                                className="rounded-full px-2 py-1 hover:bg-white/20"
                                variant="invisible"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        }
                        asChild
                    />
                </div>
            )}
        </div>
    );
};

export default EventContentFooter;
